import type { ReactNode } from "react";
import { edImage } from "@/lib/images/ed-image";
import { cn } from "@/lib/utils";

const AVATAR_TONES = [
	{ background: "var(--ed-avatar-1)", color: "var(--ed-text-inverse)" },
	{ background: "var(--ed-avatar-2)", color: "var(--ed-text-inverse)" },
	{ background: "var(--ed-avatar-3)", color: "var(--ed-text-primary)" },
];

export function initialsFor(name: string | null | undefined) {
	const parts = (name ?? "")
		.trim()
		.split(/[\s_.-]+/)
		.filter(Boolean);
	if (!parts.length) return "··";
	const first = parts[0]?.[0] ?? "";
	const second =
		parts.length > 1 ? (parts[1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
	return `${first}${second}`.toUpperCase();
}

export interface EditorialPerson {
	name: string;
	avatarUrl?: string | null;
}

export function Avatar({
	person,
	tone = 0,
	size = 20,
}: {
	person: EditorialPerson;
	tone?: number;
	size?: number;
}) {
	const colours = AVATAR_TONES[tone % AVATAR_TONES.length];
	return (
		<span
			className="ed-avatar"
			style={{ ...colours, width: size, height: size }}
			title={person.name}
		>
			{person.avatarUrl ? (
				<img {...edImage(person.avatarUrl, size)} alt="" />
			) : (
				<span aria-hidden>{initialsFor(person.name)}</span>
			)}
		</span>
	);
}

export function AvatarStack({ people }: { people: EditorialPerson[] }) {
	if (!people.length) return null;
	return (
		<span style={{ display: "inline-flex" }} aria-hidden>
			{people.slice(0, 3).map((person, index) => (
				<Avatar key={`${person.name}-${index}`} person={person} tone={index} />
			))}
		</span>
	);
}

/** Date and optional link, in Meta. */
export function MetaLine({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return <div className={cn("ed-meta ed-row", className)}>{children}</div>;
}

/**
 * Page title and intro for a page in the column, with an optional eyebrow.
 * The eyebrow is only for context the title doesn't give (where the page
 * sits, e.g. "Scenes / Tones of London / Calendar" over "October 2026");
 * it is dropped when it only repeats the title.
 */
function repeatsTitle(crumb: string, title: string) {
	const words = (value: string) =>
		value
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, " ")
			.trim();
	const eyebrow = words(crumb);
	const heading = words(title);
	return (
		eyebrow === heading ||
		eyebrow === `${heading} all topics` ||
		eyebrow === `${heading} all`
	);
}

export function PageIntro({
	crumb,
	title,
	count,
	intro,
	bar,
	children,
}: {
	crumb?: string;
	title: string;
	/** A count shown in grey beside the title, e.g. the number of stories. */
	count?: ReactNode;
	/** One short line under the title. */
	intro?: ReactNode;
	/**
	 * The page's tools bar (`PageBar`): sort, view and filters. It follows the
	 * title and stays pinned at the top while the page scrolls. Pages with a
	 * bar drop the intro; `children` (e.g. a composer) sit under the bar.
	 */
	bar?: ReactNode;
	children?: ReactNode;
}) {
	const head = (
		// The page header: the title in small caps with its count, then the
		// intro and the page's own tools when there is no bar.
		<header
			className={cn("ed-entry ed-page-head", bar && "ed-page-head--bar")}
			data-ed-entry
		>
			{crumb && !repeatsTitle(crumb, title) ? (
				<span className="ed-meta ed-page-crumb">{crumb}</span>
			) : null}
			<h1 className="ed-page-title">
				{title}
				{count !== undefined && count !== null ? (
					<span className="ed-secondary">{count}</span>
				) : null}
			</h1>
			{intro ? <p className="ed-page-intro">{intro}</p> : null}
			{bar ? null : children}
		</header>
	);
	if (!bar) return head;
	return (
		<>
			{head}
			{bar}
			{children ? <div className="ed-page-after">{children}</div> : null}
			{/* Sets the space between the header group and the first entry. */}
			<div
				aria-hidden
				className={cn(
					"ed-page-head-end",
					Boolean(children) && "ed-page-head-end--after",
				)}
			/>
		</>
	);
}

export function EmptyNote({ children }: { children: ReactNode }) {
	return (
		<p className="ed-body ed-secondary" style={{ margin: 0 }}>
			{children}
		</p>
	);
}

const MONTHS = [
	"JAN",
	"FEB",
	"MAR",
	"APR",
	"MAY",
	"JUN",
	"JUL",
	"AUG",
	"SEP",
	"OCT",
	"NOV",
	"DEC",
];

/** The one date format in the editorial shell: "26 OCT. 26" (dd MON. yy). */
export function formatEditorialDate(iso: string | null | undefined) {
	if (!iso) return "";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "";
	const day = String(date.getDate()).padStart(2, "0");
	const year = String(date.getFullYear() % 100).padStart(2, "0");
	return `${day} ${MONTHS[date.getMonth()]}. ${year}`;
}

/** A heart drawn at cap height so it sits level with Meta text. */
export function HeartIcon({ filled = true }: { filled?: boolean }) {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 12 11"
			width="0.9em"
			height="0.82em"
			style={{ display: "inline-block", verticalAlign: "-0.06em" }}
		>
			<path
				d="M6 10.2 1.4 5.8A2.8 2.8 0 0 1 6 2.1a2.8 2.8 0 0 1 4.6 3.7Z"
				fill={filled ? "currentColor" : "none"}
				stroke="currentColor"
				strokeWidth="1.2"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export function formatFileSize(bytes: number) {
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
